import React, { useState, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import {
  Globe, Plus, Edit, Trash2, CheckCircle,
  ArrowUpRight, Filter, Search, Settings,
  DollarSign, Flag, AlertCircle, Users, Loader2
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import RegionalPricingModal from "./RegionalPricingModal";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import {
  fetchRegionalPricings,
  deleteRegionalPricing,
  clearError
} from "../../redux/Slices/RegionalPricingSlice";
import {
  RegionalPricingTypeEnum
} from "../../modules/shared/model";
import type { RegionalPricing } from "../../modules/shared/model";

const RegionalPricingManagement = () => {
  const dispatch = useAppDispatch();
  const { regionalPricings, loading, error, totalCount } = useAppSelector(state => state.regionalPricings);
  
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState<RegionalPricing | null>(null);

  // Load regional pricings on component mount
  useEffect(() => {
    dispatch(fetchRegionalPricings({ page: 1, limit: 50 }));
  }, [dispatch]);

  // Clear error when component unmounts or modal closes
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this regional pricing?")) {
      try {
        await dispatch(deleteRegionalPricing(id));
      } catch (err) {
        console.error("Failed to delete regional pricing:", err);
      }
    }
  };

  const handleEdit = (region: RegionalPricing) => {
    setShowEditModal(region);
  };

  const handleModalClose = () => {
    setShowEditModal(null);
    setShowAddModal(false);
  };

  const handleSuccess = () => {
    // Refresh the data after successful create/update
    dispatch(fetchRegionalPricings({ page: 1, limit: 50 }));
  };

  // Filter regions based on search and country
  const filteredRegions = regionalPricings.filter(region => {
    const matchesSearch = region.name?.toLowerCase().includes(searchQuery.toLowerCase()) || false;
    const matchesCountry = selectedCountry === "all" || region.country?.name?.toLowerCase() === selectedCountry.toLowerCase();
    return matchesSearch && matchesCountry;
  });

  const RegionCard = ({ region }: { region: RegionalPricing }) => (
    <Card className="p-6 space-y-6">
      <div className="flex justify-between items-start"  style={{ textAlign: "left" }}>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-semibold">{region.name}</h3>
            <Badge variant={undefined} className="bg-blue-100 text-blue-700">
              {region.type?.id === RegionalPricingTypeEnum.City ? "City" : "Metropolitan"}
            </Badge>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Flag className="h-4 w-4 text-gray-400" />
            <span className="text-gray-600">{region.country?.name || "N/A"}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(region)}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600"
            onClick={() => handleDelete(region.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <h4 className="font-medium mb-3">Subscription Plans</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Basic</span>
              <Badge variant={undefined} className="bg-blue-100 text-blue-700">
                ${region.subscriptionPlanBasic || 0}
              </Badge>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span>Platform</span>
              <Badge variant={undefined} className="bg-blue-100 text-blue-700">
                ${region.subscriptionPlanPlatform || 0}
              </Badge>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span>Enterprise</span>
              <Badge variant={undefined} className="bg-blue-100 text-blue-700">
                ${region.subscriptionPlanEnterprise || 0}
              </Badge>
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-medium mb-3">Service Charges</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Emergency</span>
              <Badge variant={undefined} className="bg-purple-100 text-purple-700">
                ${region.ServiceChargesEmergency || 0}
              </Badge>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span>Transaction</span>
              <Badge variant={undefined} className="bg-purple-100 text-purple-700">
                ${region.ServiceChargesTransaction || 0}
              </Badge>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span>Platform</span>
              <Badge variant={undefined} className="bg-purple-100 text-purple-700">
                ${region.ServiceChargesPlatform || 0}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-medium mb-3">Price Modifiers</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span>Weekend Rates</span>
            <Badge variant={undefined} className={region.enableWeekendRates ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
              {region.enableWeekendRates ? "Enabled" : "Disabled"}
            </Badge>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span>Holiday Rates</span>
            <Badge variant={undefined} className={region.enableHolidayRates ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
              {region.enableHolidayRates ? "Enabled" : "Disabled"}
            </Badge>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span>Peak Hours</span>
            <Badge variant={undefined} className={region.enablePeakHours ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
              {region.enablePeakHours ? "Enabled" : "Disabled"}
            </Badge>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span>Seasonal Pricing</span>
            <Badge variant={undefined} className={region.enableSeasonalPricing ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
              {region.enableSeasonalPricing ? "Enabled" : "Disabled"}
            </Badge>
          </div>
        </div>
      </div>

      <div className="pt-4 border-t grid grid-cols-2 gap-4"  style={{ textAlign: "left" }}>
        <div className="flex items-center gap-2">
          <Flag className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-600">
            {region.country?.name || "N/A"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-600">
            {region.currency?.symbol || "$"}{region.currency?.code || "USD"}
          </span>
        </div>
      </div>
    </Card>
  );


  return (
    <div className="min-h-screen bg-gray-50 flex flex-col"  style={{ textAlign: "left" }}>
      <AdminHeader />
      
      <main className="flex-1 lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Regional Pricing</h1>
                <p className="text-gray-600 mt-1">Manage pricing variations across different regions</p>
              </div>
              <Button 
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Region
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Total Regions</p>
                    <p className="text-2xl font-semibold mt-1">{totalCount || 0}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Globe className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Active Regions</p>
                    <p className="text-2xl font-semibold mt-1">{regionalPricings.length}</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">City Regions</p>
                    <p className="text-2xl font-semibold mt-1">
                      {regionalPricings.filter(r => r.type?.id === RegionalPricingTypeEnum.City).length}
                    </p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Settings className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Metropolitan Regions</p>
                    <p className="text-2xl font-semibold mt-1">
                      {regionalPricings.filter(r => r.type?.id === RegionalPricingTypeEnum.Metropolitan).length}
                    </p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <ArrowUpRight className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Search and Filters */}
            <Card className="p-4">
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="relative flex-1 sm:min-w-[300px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search regions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-2"
                  >
                    <Filter className="h-5 w-5" />
                    Filters
                  </Button>
                </div>
                
                <select
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                  className="px-4 py-2 border rounded-lg"
                >
                  <option value="all">All Countries</option>
                  <option value="canada">Canada</option>
                  <option value="usa">United States</option>
                </select>
              </div>
            </Card>

            {/* Loading State */}
            {loading && (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-600">Loading regional pricings...</span>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <span className="text-red-700">{error}</span>
                </div>
              </div>
            )}

            {/* Regions Grid */}
            {!loading && !error && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filteredRegions.length > 0 ? (
                  filteredRegions.map(region => (
                    <RegionCard key={region.id} region={region} />
                  ))
                ) : (
                  <div className="col-span-full text-center py-12">
                    <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No regional pricings found</h3>
                    <p className="text-gray-600 mb-4">
                      {searchQuery || selectedCountry !== "all" 
                        ? "Try adjusting your search or filters" 
                        : "Get started by creating your first regional pricing"
                      }
                    </p>
                    {!searchQuery && selectedCountry === "all" && (
                      <Button onClick={() => setShowAddModal(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Regional Pricing
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Modals */}
      <AnimatePresence>
        <RegionalPricingModal
          region={showEditModal}
          isOpen={!!showEditModal || showAddModal}
          onClose={handleModalClose}
          onSuccess={handleSuccess}
        />
      </AnimatePresence>
    </div>
  );
};

export default RegionalPricingManagement;